import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions,
  Button, TextField, Select, MenuItem, FormControl,
  FormHelperText, Typography, Box, Divider, IconButton,
  ToggleButton, ToggleButtonGroup, Grid, InputAdornment,
} from '@mui/material';
import {
  RadioGroup, Radio, FormControlLabel
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AlertMessage from './AlertMessage';

const SOURCE_DIRECT = "direct"; // New Member - Ideal Onboarding
const SOURCE_LEGACY = "legacy"; // Exisiting Member - Legacy Member onboarding

const GENDER_CHOICES = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'other', label: 'Other' },
];

const PAYMENT_MODE_CHOICES = [
  { value: 'online', label: 'Online' },
  { value: 'cash', label: 'Cash' },
];

const today = new Date().toISOString().split('T')[0];

const initialDirect = {
  full_name: '',
  phone: '',
  gender: GENDER_CHOICES[0].value,
  joined_at: today,
  plan: '',
  start_date: today,
  payment_mode: 'online',
  payment_date: today,
};

const initialLegacy = {
  full_name: '',
  phone: '',
  gender: GENDER_CHOICES[0].value,
  plan: '',
  start_date: today,
  payment_mode: 'online',
  payment_date: today,
};

// ToDo: Should we rely on backend for this to ensure consistency
const getSubscriptionEndDate = (startDate, validityMonths) => {
  const start = new Date(startDate);

  // Preserve original day
  const originalDay = start.getDate();

  // Move to first day to avoid JS month overflow issues
  const nextCycle = new Date(start);
  nextCycle.setDate(1);

  // Add validity months
  nextCycle.setMonth(nextCycle.getMonth() + validityMonths);

  // Get max valid day in target month
  const lastDayOfMonth = new Date(
    nextCycle.getFullYear(),
    nextCycle.getMonth() + 1,
    0
  ).getDate();

  // Restore closest possible original day
  nextCycle.setDate(
    Math.min(originalDay, lastDayOfMonth)
  );

  // Inclusive end date
  nextCycle.setDate(nextCycle.getDate() - 1);

  return nextCycle;
};

function formatDateLabel(date) {
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

export default function MemberOnboardModal({
  open,
  plans = [],
  onClose,
  onSave,
}) {
  const [source, setSource] = useState(SOURCE_DIRECT);
  const [formData, setFormData] = useState(initialDirect);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const isDirect = source === SOURCE_DIRECT;

  const planEndDate = React.useMemo(() => {
    const plan = plans?.find(p => p.id === formData.plan);
    if (!formData.start_date || !plan) return '';
    return getSubscriptionEndDate(formData.start_date, plan.validity_months);
  }, [formData.plan, formData.start_date, plans]);

  const isStartDateFuture = formData.start_date && 
    new Date(formData.start_date) >= new Date().setHours(0, 0, 0, 0);

  const finalAmount = React.useMemo(() => {
    const joining = (parseFloat(formData.joining_fee) || 0) - (parseFloat(formData.joining_discount) || 0);
    const sub = (parseFloat(formData.fee) || 0) - (parseFloat(formData.fee_discount) || 0);
    return isDirect ? joining + sub : sub;
  }, [formData.joining_fee, formData.joining_discount, formData.fee, formData.fee_discount]);

  useEffect(() => {
    if (!open) return;
    
    const defaultPlan = plans?.[0];
    setFormData(prev => ({
      ...prev,
      plan: defaultPlan?.id ?? '',
      joining_fee: defaultPlan?.joining_fee ?? '',
      joining_discount: defaultPlan?.joining_discount ?? '',
      fee: defaultPlan?.fee ?? '',
      fee_discount: defaultPlan?.fee_discount ?? '',
    }));
    setErrors({});
  }, [open, plans]);

  useEffect(() => {
    if (isDirect) return;
    const plan_dict = plans?.find(p => p.id === formData.plan);
    setFormData(prev => ({
      ...prev,
      joining_fee: '',
      joining_discount: '',
      fee: isStartDateFuture ? (plan_dict?.fee ?? '') : '',
      fee_discount: isStartDateFuture ? (plan_dict?.fee_discount ?? '') : '',
    }));
  }, [isStartDateFuture, isDirect]);

  const handleTypeChange = (_, newType) => {
    if (!newType) return;
    const plan_dict = plans?.find(p => p.id === formData.plan);
    setSource(newType);
    setFormData(newType === SOURCE_DIRECT ?
      {
        ...initialDirect,
        plan: plan_dict.id,
        joining_fee: plan_dict?.joining_fee ?? '',
        joining_discount: plan_dict?.joining_discount ?? '',
        fee: plan_dict?.fee ?? '',
        fee_discount: plan_dict?.fee_discount ?? '',
      } :
      {
        ...initialLegacy,
        plan: plan_dict.id,
        joining_fee: plan_dict?.joining_fee ?? '',
        joining_discount: plan_dict?.joining_discount ?? '',
        fee: plan_dict?.fee ?? '',
        fee_discount: plan_dict?.fee_discount ?? '',
      }
    );
    setErrors({});
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: undefined }));
  };

  const handlePlanChange = (_, value) => {
    const plan_dict = plans?.find(p => p.id === value);
    setFormData(prev => ({
      ...prev,
      plan: value,
      ...(plan_dict && {
        joining_fee: plan_dict.joining_fee,
        joining_discount: plan_dict.joining_discount,
        fee: plan_dict.fee,
        fee_discount: plan_dict.fee_discount,
      }),
    }));
    setErrors(prev => ({ ...prev, plan: undefined }));
  };

  const handleSave = async () => {
    if (isLoading) return;
    setIsLoading(true);

    try {
      await onSave(
        {
          ...formData,
          phone: formData.phone ? `+91${formData.phone}` : '',
          source: source,
          is_start_date_future: isStartDateFuture,
          final_amount: finalAmount,
          fe_calc: {
            end_date: planEndDate?.toISOString().split('T')[0],
          },
        },
        () => {
          setFormData(isDirect ? initialDirect : initialLegacy);
          setErrors({});
        },
        (errs) => setErrors(errs),
      );
    } finally {
      setIsLoading(false);
    }
  };

  const field = (label, attr, type = 'text', required = false, placeholder = '', textFieldProps = {}) => (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={0.25}>
        {label}{required && <Typography component="span" color="error" ml={0.3}>*</Typography>}
      </Typography>
      <TextField
        fullWidth size="small"
        type={type}
        value={formData[attr] ?? ''}
        onChange={e => handleChange(attr, e.target.value)}
        error={Boolean(errors[attr])}
        helperText={errors[attr]?.[0]}
        placeholder={placeholder}
        InputLabelProps={type === 'date' ? { shrink: true } : undefined}
        {...textFieldProps}
      />
    </Box>
  );

  const number_field = (label, attr, required = false, editable = true, min = 0, max = undefined) => {
    const value = parseFloat(formData[attr]) || 0;
    const sanitize = (val) => {
      let v = Number.isNaN(val) ? min : Math.max(min, val);
      if (max !== undefined) v = Math.min(v, max);
      return v;
    };

    return (
      <Box>
        <Typography variant="body2" color="text.secondary" mb={0.25}>
          {label}{required && <Typography component="span" color="error" ml={0.3}>*</Typography>}
        </Typography>
        <TextField
          fullWidth size="small"
          type="number"
          value={value}
          onChange={e => handleChange(attr, e.target.value)}
          onBlur={e => handleChange(attr, sanitize(parseFloat(e.target.value)))}
          error={Boolean(errors[attr])}
          helperText={errors[attr]?.[0]}
          disabled={!editable}
        />
      </Box>
    );
  };

  const selectField = (label, attr, choices, required = false, onChange = handleChange) => (
    <Box>
      <Typography variant="body2" color="text.secondary" mb={0.25}>
        {label}{required && <Typography component="span" color="error" ml={0.3}>*</Typography>}
      </Typography>
      <FormControl fullWidth size="small" error={Boolean(errors[attr])}>
        <Select
          value={formData[attr] ?? ''}
          onChange={e => onChange(attr, e.target.value)}
          displayEmpty
        >
          {!required && (
            <MenuItem value=""><em style={{ color: '#999' }}>Select</em></MenuItem>
          )}
          {choices.map(c => (
            <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
          ))}
        </Select>
        {errors[attr] && <FormHelperText>{errors[attr]?.[0]}</FormHelperText>}
      </FormControl>
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
          // boxShadow: 24
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
        <Typography variant="h3">Add member</Typography>
        {true && ( // ToDo: !isSubmitting
          <IconButton edge="end" onClick={onClose} aria-label="close">
            <CloseIcon />
          </IconButton>
        )}
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ pt: 2 }}>
        <Box display="flex" flexDirection="column" gap={1.5}>
          {/* Onboarding Type - Direct/Legacy */}
          <FormControl component="fieldset">
            <RadioGroup
              row
              value={source}
              onChange={(e) => handleTypeChange(e, e.target.value)}
            >
              <FormControlLabel
                value={SOURCE_DIRECT}
                control={<Radio size="small" />}
                label={<Typography variant="body2">New member</Typography>}
              />
              <FormControlLabel
                value={SOURCE_LEGACY}
                control={<Radio size="small" />}
                label={<Typography variant="body2">Existing member</Typography>}
              />
            </RadioGroup>
          </FormControl>

          {!isDirect && (
            <AlertMessage
              severity="warning"
              message="Joining date and past payments are not recorded for existing members."
            />
          )}


          {/* Member details */}
          {field('Name', 'full_name', 'text', true, 'Full name')}

          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              {field(
                'Phone', 'phone', 'tel', true, '9876543210',
                {
                  InputProps: {
                    startAdornment: (
                      <InputAdornment position="start">
                        +91
                      </InputAdornment>
                    ),
                  },
                  inputProps: {
                    maxLength: 10,
                    inputMode: 'numeric',
                  },
                }
              )}
            </Grid>
            <Grid item xs={6}>
              {selectField('Gender', 'gender', GENDER_CHOICES, true)}
            </Grid>
          </Grid>

          {/* Joining date — direct only */}
          {isDirect && field('Joining date', 'joined_at', 'date', true)}


          {/* Plan + subcription details */}
          <Divider sx={{ mt: 1 }}>
            <Typography variant="body1" color="text.secondary">Plan Detail</Typography>
          </Divider>

          {/* Subscription */}
          <Grid container spacing={1.5}>
            <Grid item xs={6}>
              {selectField('Plan', 'plan', plans.map(p => ({ value: p.id, label: p.name })), true, handlePlanChange)}
            </Grid>
            <Grid item xs={6}>
              {field('Start date', 'start_date', 'date', true)}
            </Grid>
          </Grid>

          {planEndDate && (
            <AlertMessage
              severity="info"
              message={`Valid till ${formatDateLabel(planEndDate)}`}
            />
          )}


          {/* Fees + payment details */}
          <Divider sx={{ mt: 1 }}>
            <Typography variant="body1" color="text.secondary">Payment Detail</Typography>
          </Divider>
          {isDirect && (
            <>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  {number_field('Joining fee', 'joining_fee', false, false )}
                </Grid>
                <Grid item xs={6}>
                  {number_field('Joining discount', 'joining_discount', false, true, 0, parseFloat(formData.joining_fee) || 0 )}
                </Grid>
              </Grid>
            </>
          )}

          { (isDirect || isStartDateFuture) ? (
            <>
              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  {number_field('Subscription Fee', 'fee', false, false )}
                </Grid>
                <Grid item xs={6}>
                  {number_field('Subscription discount', 'fee_discount', false, true, 0, parseFloat(formData.fee) || 0 )}
                </Grid>
              </Grid>

              <AlertMessage
                severity="info"
                message={`Total Amount to be paid = ₹ ${finalAmount} /-`}
              />

              <Grid container spacing={1.5}>
                <Grid item xs={6}>
                  {selectField('Payment mode', 'payment_mode', PAYMENT_MODE_CHOICES, true)}
                </Grid>
                <Grid item xs={6}>
                  {field('Payment date', 'payment_date', 'date', true)}
                </Grid>
              </Grid>
            </>
          ) : (
            <AlertMessage
              severity="warning"
              message="Adding backlog entry. No payment will be recorded."
            />
          )}

        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
        >
          Cancel
        </Button>
        <Button
          disabled={isLoading}
          onClick={handleSave}
          variant="contained"
          color="primary"
        >
          {isLoading ? "Saving..." : "Save"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
